import { X } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

interface FileExtensionsInputProps {
  placeholder?: string;
  extensions: string[];
  onExtensionsChange: (value: string[]) => void;
  endDecorator?: React.ReactNode;
  disabled?: boolean;
}

function addDotPrefixAndRemoveSpaces(input: string): string {
  const trimmedAndNoSpacesInput = input.replace(/\s/g, "");
  if (!trimmedAndNoSpacesInput.startsWith(".")) {
    return "." + trimmedAndNoSpacesInput;
  }
  return trimmedAndNoSpacesInput;
}

const FileExtensionsInput: React.FC<FileExtensionsInputProps> = ({
  placeholder = "",
  extensions,
  onExtensionsChange,
  endDecorator = null,
  disabled = false,
}) => {
  const commit = (value: string) => {
    if (!value) return;
    onExtensionsChange(
      Array.from(new Set([...extensions, addDotPrefixAndRemoveSpaces(value)])),
    );
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    e.target.value = "";
    commit(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.code === "Space") {
      e.preventDefault();
      const target = e.target as HTMLInputElement;
      const value = target.value;
      target.value = "";
      commit(value);
    }
  };

  return (
    <div
      className={cn(
        "flex h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-0.5 text-sm shadow-xs transition-[color,box-shadow] overflow-hidden",
        "focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
        "dark:bg-input/30",
        disabled && "opacity-50 pointer-events-none",
      )}
    >
      {extensions.map((extension) => (
        <span
          key={extension}
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-xs font-medium",
            "bg-primary/15 text-primary dark:bg-primary/25",
          )}
        >
          {extension}
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full hover:bg-primary/20 p-0.5"
            onClick={(e) => {
              e.preventDefault();
              onExtensionsChange(extensions.filter((f) => f !== extension));
            }}
            disabled={disabled}
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        className="min-w-16 flex-1 bg-transparent outline-none placeholder:text-muted-foreground text-sm"
        placeholder={placeholder}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {endDecorator}
    </div>
  );
};

export default FileExtensionsInput;
